import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { CompactStepper } from "@/components/ui/Stepper";
import { Loader2, Plus, Trash2 } from "lucide-react";

// Subtipos de tareas
const TASK_SUBTYPES = {
  administrative: [
    { value: "training", label: "Capacitación" },
    { value: "report", label: "Informe" },
    { value: "audit", label: "Auditoría" },
    { value: "meeting", label: "Reunión" },
    { value: "documentation", label: "Documentación" },
  ],
  operative: [
    { value: "cleaning", label: "Limpieza" },
    { value: "maintenance", label: "Mantenimiento" },
    { value: "organization", label: "Organización" },
    { value: "inventory", label: "Inventario" },
    { value: "equipment_check", label: "Revisión de Equipos" },
  ],
};

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateTaskDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateTaskDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Stepper
  const getCurrentStep = () => {
    if (!formData.title.trim()) return 0;
    if (!formData.taskType) return 1;
    return 2;
  };
  const stepLabels = ['Titulo', 'Tipo', 'Detalles'];
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: string) => {
    let error = '';
    if (name === 'title' && !value.trim()) {
      error = 'El titulo es requerido';
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, formData[name as keyof typeof formData]?.toString() || '');
  };
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    taskType: "administrative" as "administrative" | "operative",
    taskSubtype: "",
    assignedTo: "",
    priority: "normal",
    dueDate: "",
    estimatedDuration: "",
    notes: "",
  });
  const [steps, setSteps] = useState<string[]>([]);
  const [newStep, setNewStep] = useState("");

  // Fetch operators
  const { data: operators } = useQuery({
    queryKey: ["operators-for-tasks"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, employee_id")
        .eq("is_active", true)
        .order("full_name", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Fetch internal customers (ADM/OPE)
  const { data: internalCustomers } = useQuery({
    queryKey: ["internal-customers"],
    queryFn: async () => {
      const { data, error } = await api
        .from("customers")
        .select("id, company_name, codificacion")
        .in("codificacion", ["ADM", "OPE"])
        .eq("is_active", true);
      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("El título es requerido");
      return;
    }

    if (!formData.taskSubtype) {
      toast.error("Selecciona el tipo de tarea");
      return;
    }

    setLoading(true);
    try {
      // Get customer based on task type
      const customer = internalCustomers?.find((c: any) =>
        formData.taskType === "administrative"
          ? c.codificacion === "ADM"
          : c.codificacion === "OPE"
      );

      // Create task
      const { data: newTask, error } = await api
        .from("internal_tasks")
        .insert({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          task_type: formData.taskType,
          task_subtype: formData.taskSubtype,
          customer_id: customer?.id || null,
          assigned_to: formData.assignedTo || null,
          priority: formData.priority,
          due_date: formData.dueDate || null,
          estimated_duration_minutes: formData.estimatedDuration
            ? parseInt(formData.estimatedDuration)
            : null,
          notes: formData.notes.trim() || null,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Create steps if any
      if (steps.length > 0 && newTask) {
        const stepsData = steps.map((step, index) => ({
          task_id: newTask.id,
          step_number: index + 1,
          description: step,
        }));

        const { error: stepsError } = await api
          .from("internal_task_steps")
          .insert(stepsData);

        if (stepsError) {
          console.error("Error creating steps:", stepsError);
        }
      }

      toast.success("Tarea creada exitosamente");
      resetForm();
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Error al crear la tarea");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      taskType: "administrative",
      taskSubtype: "",
      assignedTo: "",
      priority: "normal",
      dueDate: "",
      estimatedDuration: "",
      notes: "",
    });
    setSteps([]);
    setNewStep("");
  };

  const addStep = () => {
    if (newStep.trim()) {
      setSteps([...steps, newStep.trim()]);
      setNewStep("");
    }
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const subtypes = TASK_SUBTYPES[formData.taskType];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crear Nueva Tarea</DialogTitle>
          <DialogDescription>
            Crea una tarea administrativa u operativa
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="Ej: Capacitación de seguridad"
              required
            />
          </div>

          {/* Tipo de Tarea */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categoría *</Label>
              <Select
                value={formData.taskType}
                onValueChange={(value: "administrative" | "operative") =>
                  setFormData({ ...formData, taskType: value, taskSubtype: "" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrative">Administrativa</SelectItem>
                  <SelectItem value="operative">Operativa</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select
                value={formData.taskSubtype}
                onValueChange={(value) =>
                  setFormData({ ...formData, taskSubtype: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {subtypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Descripción detallada de la tarea..."
              rows={3}
            />
          </div>

          {/* Asignado y Prioridad */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Asignar a</Label>
              <SearchableSelect
                options={operators?.map((op: any) => ({
                  value: op.id,
                  label: op.full_name
                })) || []}
                value={formData.assignedTo}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    assignedTo: value,
                  })
                }
                placeholder="Sin asignar"
                searchPlaceholder="Buscar persona..."
                clearText="Sin asignar"
              />
            </div>

            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baja</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fecha y Duración */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dueDate">Fecha Límite</Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) =>
                  setFormData({ ...formData, dueDate: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimatedDuration">Duración Estimada (min)</Label>
              <Input
                id="estimatedDuration"
                type="number"
                min="1"
                value={formData.estimatedDuration}
                onChange={(e) =>
                  setFormData({ ...formData, estimatedDuration: e.target.value })
                }
                placeholder="60"
              />
            </div>
          </div>

          {/* Checklist */}
          <div className="space-y-2">
            <Label>Checklist de Pasos</Label>
            <div className="flex gap-2">
              <Input
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                placeholder="Agregar paso..."
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addStep())}
              />
              <Button type="button" variant="outline" size="icon" onClick={addStep}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {steps.length > 0 && (
              <div className="space-y-2 mt-2">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-muted rounded-md"
                  >
                    <span className="text-sm text-muted-foreground w-6">
                      {index + 1}.
                    </span>
                    <span className="flex-1 text-sm">{step}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => removeStep(index)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notas Adicionales</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Notas o comentarios..."
              rows={2}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-magma hover:shadow-magma-hover"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Tarea
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
